<template>
  <DataTable 
    :value="sortedProjects" 
    :paginator="true"
    :rows="20"
    :rows-per-page-options="[10, 20, 50, 100]"
    striped-rows
    :row-hover="true"
    @row-click="onRowClick"
  >
    <!-- Project Name Column -->
    <Column
      field="name"
      header="Project Name"
      sortable
    >
      <template #body="{ data }">
        <div class="flex items-center gap-2">
          <Button 
            v-tooltip.top="data.is_favorite ? 'Remove from favorites' : 'Add to favorites'" 
            :icon="data.is_favorite ? 'pi pi-star-fill' : 'pi pi-star'"
            :class="data.is_favorite ? 'text-yellow-500' : 'text-gray-400'" 
            text 
            size="small"
            @click.stop="toggleFavorite(data)"
          />
          <div class="flex-1">
            <div class="font-medium text-gray-900">
              {{ data.name }}
            </div>
            <div class="text-sm text-gray-500">
              {{ data.project_number }}
            </div>
          </div>
        </div>
      </template>
    </Column>

    <!-- Company Column -->
    <Column
      field="company"
      header="Company"
      sortable
    >
      <template #body="{ data }">
        <span class="text-sm text-gray-900">{{ data.company?.company_name || data.client?.company_name || 'No company' }}</span>
      </template>
    </Column>

    <!-- Start Date Column -->
    <Column
      field="start_date"
      header="Start Date"
      sortable
    >
      <template #body="{ data }">
        <div class="bg-blue-50 rounded-lg p-2 text-center min-w-[60px]">
          <div class="text-lg font-bold text-blue-600">
            {{ formatDate(data.start_date || data.start).day }}
          </div>
          <div class="text-xs font-semibold text-blue-500">
            {{ formatDate(data.start_date || data.start).month }}
          </div>
          <div class="text-xs text-blue-400">
            {{ formatDate(data.start_date || data.start).year }}
          </div>
        </div>
      </template>
    </Column>

    <!-- End Date Column -->
    <Column
      field="end_date"
      header="End Date"
      sortable
    >
      <template #body="{ data }">
        <div class="bg-green-50 rounded-lg p-2 text-center min-w-[60px]">
          <div class="text-lg font-bold text-green-600">
            {{ formatDate(data.end_date || data.end).day }}
          </div>
          <div class="text-xs font-semibold text-green-500">
            {{ formatDate(data.end_date || data.end).month }}
          </div>
          <div class="text-xs text-green-400">
            {{ formatDate(data.end_date || data.end).year }}
          </div>
        </div>
      </template>
    </Column>

    <!-- Status Column -->
    <Column
      field="status"
      header="Status"
      sortable
    >
      <template #body="{ data }">
        <Badge 
          :value="data.status?.global_status?.name || data.status?.name || data.status" 
          :severity="projectSeverity(data.status?.global_status?.name || data.status?.name || data.status)"
          :style="getBadgeStyle(data.status)"
        />
      </template>
    </Column>
  </DataTable>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue';
import { projectSeverity } from '@/shared/DataTableFunctions';
import { useRouter } from 'vue-router';
import { useToastService } from '@/composables/useToastService';
import { v1Patch } from '@/services/v1Api'

const router = useRouter();
const toast = useToastService();

// Define props for accepting projects data
const props = defineProps({
    projects: {
        type: Array,
        default: () => [],
    },
    isWarehouse: {
        type: Boolean,
        default: false
    }
});

// Define emits
const emit = defineEmits(['refresh-projects']);

// Function to get badge style with dynamic colors from API
const getBadgeStyle = (status) => {
    const style = {};
    
    // Check for nested global_status structure first
    if (status?.global_status?.color) {
        style.backgroundColor = status.global_status.color;
    } else if (status?.color) {
        style.backgroundColor = status.color;
    }
    
    // Check for text color
    if (status?.global_status?.text_color) {
        style.color = status.global_status.text_color;
    } else if (status?.text_color) {
        style.color = status.text_color;
    }
    
    return style;
};

// Format date for display
const formatDate = (dateString) => {
    try {
        if (!dateString) return { day: '--', month: '---', year: '----' };
        
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return { day: '--', month: '---', year: '----' };
        
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        
        return { day, month, year };
    } catch (error) {
        console.error('Error formatting date:', error);
        return { day: '--', month: '---', year: '----' };
    }
};

// Use projects as-is to preserve API order
const sortedProjects = computed(() => {
    // Check if projects is an array and not empty
    if (!Array.isArray(props.projects) || props.projects.length === 0) {
        return [];
    }
    
    // Return projects in the order received from API (no additional sorting)
    return [...props.projects];
});

// Handle card click to navigate to the project detail page
const onRowClick = (event) => {
    const projectId = event.data.id;
    if (props.isWarehouse) {
        router.push(`/warehouse-project/${projectId}`);
    } else {
        router.push(`/project/${projectId}`);
    }
};

// Toggle favorite status
const toggleFavorite = async (project) => {
    try {
        const newFavoriteStatus = !project.is_favorite;
        
        // Update local state immediately for better UX
        project.is_favorite = newFavoriteStatus;
        
        // Make API call to toggle favorite status
        await v1Patch(`/api/v1/projects/${project.id}/favorite`);

        toast.add({
            severity: 'success',
            summary: newFavoriteStatus ? 'Added to Favorites' : 'Removed from Favorites',
            detail: `Project ${newFavoriteStatus ? 'added to' : 'removed from'} your favorites`,
            life: 3000
        });

        console.log(`Project ${project.id} favorite status: ${newFavoriteStatus}`);

        // Emit event to parent to refresh the project list
        emit('refresh-projects');

    } catch (error) {
        console.error('Error updating favorite status:', error);
        
        // Revert local state on error
        project.is_favorite = !project.is_favorite;
        
        if (error.response?.data?.message) {
            toast.add({
                severity: 'error',
                summary: 'Update Failed',
                detail: error.response.data.message,
                life: 5000
            });
        } else {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update favorite status',
                life: 3000
            });
        }
    }
};
</script>

<style scoped>
.text-primary {
    color: #2563eb; /* Tailwind's blue-600 */
}
</style>